import { useMemo, useState } from 'react';

import {
  ColumnDef,
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { FileFilterPopover } from './file-filter-popover';
import { formatFileSize } from './utils';
import { FileMetadata } from '../../../../../backend/src/integrations/relativity/file-metadata.entity';
import { useRemoveFiles } from '@/api/mutations/useRemoveFiles';
import formatDate from '@/helpers/formatDate';
import { cn } from '@/helpers/shadCnUtils';
import { Icon } from '@/new/components/icon';
import { MetadataDisplay } from '@/new/components/metadata-display';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TablePagination } from '@/new/components/table/table-pagination';
import { includedInArr } from '@/new/lib/table-utils';
import { File } from '@/types';

interface FileExplorerProps {
  isLoadingFiles: boolean;
  allFiles: File[];
  allDocumentsCaseId: string | null;
}

export const FileExplorer = ({ isLoadingFiles, allFiles, allDocumentsCaseId }: FileExplorerProps) => {
  const { mutate: deleteFiles } = useRemoveFiles({ caseId: allDocumentsCaseId ?? '' });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFiltersState] = useState<ColumnFiltersState>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);

  const handleBulkDelete = () => {
    deleteFiles(Object.keys(rowSelection));
    setRowSelection({});
  };

  const columns: ColumnDef<File>[] = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select file"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => {
          return (
            <div
              className="flex cursor-pointer items-center gap-1"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Name
              <Icon className="lucide--arrow-up-down h-3 w-3" />
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Icon className="lucide--file-text h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-900">{row.original.name}</span>
            {row.original.parentFiles && row.original.parentFiles.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon className="lucide--link h-3.5 w-3.5 text-gray-400 transition-colors hover:text-gray-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Attachment of{' '}
                    {row.original.parentFiles.length === 1
                      ? row.original.parentFiles[0].name
                      : `${row.original.parentFiles.length} documents`}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'type',
        header: ({ column }) => {
          return (
            <div
              className="flex cursor-pointer items-center gap-1"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Type
              <Icon className="lucide--arrow-up-down h-3 w-3" />
            </div>
          );
        },
        cell: ({ row }) => {
          const extension = row.original.name.split('.').pop()?.toUpperCase() || '';
          return (
            <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">{extension}</span>
          );
        },
        accessorFn: (row) => row.name.split('.').pop()?.toUpperCase() || '',
        filterFn: includedInArr,
      },
      {
        accessorKey: 'size',
        header: ({ column }) => {
          return (
            <div
              className="flex cursor-pointer items-center gap-1"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Size
              <Icon className="lucide--arrow-up-down h-3 w-3" />
            </div>
          );
        },
        cell: ({ row }) => <span className="text-sm text-gray-600">{formatFileSize(row.original.size)}</span>,
      },
      {
        accessorKey: 'createdDate',
        header: ({ column }) => {
          return (
            <div
              className="flex cursor-pointer items-center gap-1"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Uploaded
              <Icon className="lucide--arrow-up-down h-3 w-3" />
            </div>
          );
        },
        cell: ({ row }) => (
          <span className="text-xs text-gray-600">{formatDate(row.original.createdDate, true, true)}</span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const metadata = row.original.fileMetadata;
          const hasMetadata =
            metadata &&
            (metadata.emailSentDate ||
              metadata.documentDate ||
              (metadata.relativityFields && Object.keys(metadata.relativityFields).length > 0));

          if (!hasMetadata) return null;

          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => e.stopPropagation()}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Icon className="lucide--database h-4 w-4 text-purple-500" />
                    </TooltipTrigger>
                    <TooltipContent>View Relativity Metadata</TooltipContent>
                  </Tooltip>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="end" onClick={(e) => e.stopPropagation()}>
                <MetadataDisplay metadata={metadata as FileMetadata} compact />
              </PopoverContent>
            </Popover>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: allFiles,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFiltersState,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      sorting,
      columnFilters,
      rowSelection,
      globalFilter: searchQuery,
    },
  });

  return (
    <div className={cn('flex h-full min-h-0 w-full flex-col')}>
      <div className={cn('mt-4 flex h-full w-full flex-col gap-4')}>
        <div className="flex flex-col gap-4 border-b border-gray-200 pb-4">
          <div className="flex w-full items-center gap-4">
            <div className="flex w-full items-center justify-between gap-2 p-1">
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={selectedCount === 0 || !allDocumentsCaseId}
                >
                  <Icon className="lucide--trash h-3.5 w-3.5 transition-transform" />
                  Delete ({selectedCount})
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button disabled={isLoadingFiles}>
                      <Icon className="lucide--funnel-plus" />
                      <span>Add Filters</span>
                    </Button>
                  </PopoverTrigger>
                  <FileFilterPopover
                    files={allFiles}
                    extensionColumn={table.getColumn('type')}
                    onClearFilters={() => setColumnFiltersState([])}
                  />
                </Popover>

                <Button disabled={isLoadingFiles} onClick={() => setColumnFiltersState([])}>
                  <Icon className="lucide--funnel-x" />
                  <span>Clear All Filters</span>
                </Button>

                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className={'flex min-h-0 w-full flex-1 flex-shrink flex-col gap-2 rounded-lg border bg-white'}>
          {isLoadingFiles ? (
            <div className="flex h-full w-full items-center justify-center py-8">
              <Icon className="lucide--loader-circle animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {table.getFilteredRowModel().rows.length === 0 ? (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon className="lucide--file-text h-6 w-6" />
                    </EmptyMedia>
                    <EmptyTitle>
                      {searchQuery || columnFilters.length > 0
                        ? 'No files match your filters'
                        : 'No files uploaded yet'}
                    </EmptyTitle>
                  </EmptyHeader>
                </Empty>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() ? 'selected' : undefined}
                        className={`cursor-pointer ${row.getIsSelected() ? 'bg-blue-50' : ''}`}
                        onClick={() => row.toggleSelected()}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </div>

        <TablePagination table={table} plural="files" />
      </div>
    </div>
  );
};
